import {effect, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, Observable, of, tap} from 'rxjs';
import {environment} from '../../environments/environment';
import {CrmDocumentSummary} from '../models/api/crm/CrmDocumentSummary';
import {Group} from '../models/api/management/Group';
import {GroupPortfolioAnalysis} from '../models/api/management/GroupPortfolioAnalysis';
import {GroupPortfolioAnalysisSummary} from '../models/api/management/GroupPortfolioAnalysisSummary';
import {ManagedGroup} from '../models/api/management/ManagedGroup';
import {ManagedUser} from '../models/api/management/ManagedUser';
import {AdminUserConversationDocumentsSummary} from '../models/api/management/AdminUserConversationDocumentsSummary';
import {AdminUserDeletionPreview} from '../models/api/management/AdminUserDeletionPreview';
import {ProfileConversationSummary} from '../models/api/management/ProfileConversationSummary';
import {ToolResponse} from '@frednh/ai-agentic-core';
import {CreateGroupDto} from '../models/api/management/dto/CreateGroupDto';
import {UpsertGroupUserDto} from '../models/api/management/dto/UpsertGroupUserDto';
import {UserManagementContext} from '../models/api/management/UserManagementContext';
import {UserProfileDetails} from '../models/api/management/UserProfileDetails';
import {UserProfileUpdateInput} from '../models/api/management/dto/UserProfileUpdateInput';
import {Conversation} from '@frednh/ai-agentic-core';
import {AuthService} from './auth-service';
import {I18nService} from './i18n-service';
import {ThemeService} from './theme-service';

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  private static readonly GROUPS_URL = `${environment.apiUrl}/groups`;
  private static readonly MANAGEMENT_CONTEXT_URL = `${environment.apiUrl}/management/context`;
  private static readonly GROUP_USERS_URL = `${environment.apiUrl}/groups/{groupId}/users`;
  private static readonly GROUP_PORTFOLIO_ANALYSIS_URL = `${environment.apiUrl}/groups/{groupId}/portfolio-analysis`;
  private static readonly GROUP_PORTFOLIO_ANALYSIS_HISTORY_URL = `${environment.apiUrl}/groups/{groupId}/portfolio-analysis/history`;
  private static readonly GENERATE_GROUP_PORTFOLIO_ANALYSIS_URL = `${environment.apiUrl}/group-portfolio-analysis/generate`;
  private static readonly PROFILE_URL = `${environment.apiUrl}/profile`;
  private static readonly VISIBLE_USER_URL = `${environment.apiUrl}/users/{userId}`;
  private static readonly PROFILE_DOCUMENTS_URL = `${environment.apiUrl}/profile/documents`;
  private static readonly PROFILE_DOCUMENT_URL = `${environment.apiUrl}/profile/documents/{documentId}`;
  private static readonly PROFILE_DOCUMENT_DOWNLOAD_URL = `${environment.apiUrl}/profile/documents/{documentId}/download`;
  private static readonly PROFILE_CONVERSATIONS_URL = `${environment.apiUrl}/profile/conversations`;
  private static readonly PROFILE_CONVERSATION_URL = `${environment.apiUrl}/profile/conversations/{conversationId}`;
  private static readonly ADMIN_USER_CONVERSATION_DOCUMENTS_URL = `${environment.apiUrl}/admin/users/{userId}/conversation-documents`;
  private static readonly ADMIN_USER_DELETION_PREVIEW_URL = `${environment.apiUrl}/admin/users/{userId}/deletion-preview`;
  private static readonly ADMIN_USER_URL = `${environment.apiUrl}/admin/users/{userId}`;
  private static readonly ADMIN_USER_DOCUMENT_URL = `${environment.apiUrl}/admin/users/{userId}/documents/{documentId}`;

  private readonly _groups = signal<Group[]>([]);
  readonly groups = this._groups.asReadonly();

  private readonly _manageableGroups = signal<ManagedGroup[]>([]);
  readonly manageableGroups = this._manageableGroups.asReadonly();

  private readonly _deletableGroups = signal<Group[]>([]);
  readonly deletableGroups = this._deletableGroups.asReadonly();

  private readonly _canCreateGroups = signal<boolean>(false);
  readonly canCreateGroups = this._canCreateGroups.asReadonly();

  private readonly _hasAdminAccess = signal<boolean>(false);
  readonly hasAdminAccess = this._hasAdminAccess.asReadonly();

  private readonly _selectedGroupId = signal<string | null>(null);
  readonly selectedGroupId = this._selectedGroupId.asReadonly();

  private readonly _currentProfile = signal<UserProfileDetails | null>(null);
  readonly currentProfile = this._currentProfile.asReadonly();
  private readonly _profileDocuments = signal<CrmDocumentSummary[]>([]);
  readonly profileDocuments = this._profileDocuments.asReadonly();
  private readonly _profileDocumentsLoading = signal<boolean>(false);
  readonly profileDocumentsLoading = this._profileDocumentsLoading.asReadonly();
  private readonly _profileDocumentsError = signal<string | null>(null);
  readonly profileDocumentsError = this._profileDocumentsError.asReadonly();
  private readonly _profileConversations = signal<ProfileConversationSummary[]>([]);
  readonly profileConversations = this._profileConversations.asReadonly();
  private readonly _profileConversationsLoading = signal<boolean>(false);
  readonly profileConversationsLoading = this._profileConversationsLoading.asReadonly();
  private readonly _profileConversationsError = signal<string | null>(null);
  readonly profileConversationsError = this._profileConversationsError.asReadonly();

  private readonly _selectedGroupUsers = signal<ManagedUser[]>([]);
  readonly selectedGroupUsers = this._selectedGroupUsers.asReadonly();

  private readonly _selectedGroupUsersLoading = signal<boolean>(false);
  readonly selectedGroupUsersLoading = this._selectedGroupUsersLoading.asReadonly();

  private readonly _groupPortfolioAnalysis = signal<GroupPortfolioAnalysis | null>(null);
  readonly groupPortfolioAnalysis = this._groupPortfolioAnalysis.asReadonly();

  private readonly _groupPortfolioAnalysisHistory = signal<GroupPortfolioAnalysisSummary[]>([]);
  readonly groupPortfolioAnalysisHistory = this._groupPortfolioAnalysisHistory.asReadonly();

  private readonly _groupPortfolioAnalysisLoading = signal<boolean>(false);
  readonly groupPortfolioAnalysisLoading = this._groupPortfolioAnalysisLoading.asReadonly();
  private readonly _groupPortfolioAnalysisGenerationLoading = signal<boolean>(false);
  readonly groupPortfolioAnalysisGenerationLoading = this._groupPortfolioAnalysisGenerationLoading.asReadonly();

  private readonly _groupPortfolioAnalysisError = signal<string | null>(null);
  readonly groupPortfolioAnalysisError = this._groupPortfolioAnalysisError.asReadonly();

  private readonly _groupsLoading = signal<boolean>(false);
  readonly groupsLoading = this._groupsLoading.asReadonly();

  private readonly _profileLoading = signal<boolean>(false);
  readonly profileLoading = this._profileLoading.asReadonly();

  private readonly _profileError = signal<string | null>(null);
  readonly profileError = this._profileError.asReadonly();

  private readonly _profileSuccess = signal<string | null>(null);
  readonly profileSuccess = this._profileSuccess.asReadonly();

  private readonly _createGroupLoading = signal<boolean>(false);
  readonly createGroupLoading = this._createGroupLoading.asReadonly();

  private readonly _deleteGroupLoading = signal<boolean>(false);
  readonly deleteGroupLoading = this._deleteGroupLoading.asReadonly();

  private readonly _profileSaving = signal<boolean>(false);
  readonly profileSaving = this._profileSaving.asReadonly();

  private readonly _memberMutationLoading = signal<boolean>(false);
  readonly memberMutationLoading = this._memberMutationLoading.asReadonly();

  private readonly _groupError = signal<string | null>(null);
  readonly groupError = this._groupError.asReadonly();

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly i18n: I18nService,
    private readonly themeService: ThemeService
  ) {
    effect(() => {
      if (!this.authService.shouldShowProtectedContent()) {
        this._groups.set([]);
        this._manageableGroups.set([]);
        this._deletableGroups.set([]);
        this._canCreateGroups.set(false);
        this._hasAdminAccess.set(false);
        this._selectedGroupId.set(null);
        this._currentProfile.set(null);
        this._profileDocuments.set([]);
        this._profileDocumentsLoading.set(false);
        this._profileDocumentsError.set(null);
        this._profileConversations.set([]);
        this._profileConversationsLoading.set(false);
        this._profileConversationsError.set(null);
        this._selectedGroupUsers.set([]);
        this._selectedGroupUsersLoading.set(false);
        this._groupPortfolioAnalysis.set(null);
        this._groupPortfolioAnalysisHistory.set([]);
        this._groupPortfolioAnalysisLoading.set(false);
        this._groupPortfolioAnalysisGenerationLoading.set(false);
        this._groupPortfolioAnalysisError.set(null);
        this._groupError.set(null);
        this._profileError.set(null);
        this._profileSuccess.set(null);
        this._groupsLoading.set(false);
        this._profileLoading.set(false);
        this._createGroupLoading.set(false);
        this._deleteGroupLoading.set(false);
        this._profileSaving.set(false);
        this._memberMutationLoading.set(false);
        return;
      }

      if (!this.authService.isAuthenticated()) {
        return;
      }

      this.getGroups();
      this.getCurrentProfile();
      this.getProfileDocuments();
      this.getProfileConversations();
    });

    effect(() => {
      if (!this.authService.shouldShowProtectedContent()) {
        this._selectedGroupUsers.set([]);
        this._selectedGroupUsersLoading.set(false);
        return;
      }

      if (!this.authService.isAuthenticated()) {
        return;
      }

      const selectedGroupId = this._selectedGroupId();
      if (!selectedGroupId) {
        this._selectedGroupUsers.set([]);
        this._selectedGroupUsersLoading.set(false);
        return;
      }

      this.getSelectedGroupUsers(selectedGroupId);
    });

    effect(() => {
      if (!this.authService.shouldShowProtectedContent()) {
        this._groupPortfolioAnalysis.set(null);
        this._groupPortfolioAnalysisHistory.set([]);
        this._groupPortfolioAnalysisLoading.set(false);
        this._groupPortfolioAnalysisGenerationLoading.set(false);
        this._groupPortfolioAnalysisError.set(null);
        return;
      }

      if (!this.authService.isAuthenticated()) {
        return;
      }

      const selectedGroupId = this._selectedGroupId();
      const language = this.i18n.language();
      if (!selectedGroupId) {
        this._groupPortfolioAnalysis.set(null);
        this._groupPortfolioAnalysisHistory.set([]);
        this._groupPortfolioAnalysisLoading.set(false);
        this._groupPortfolioAnalysisGenerationLoading.set(false);
        this._groupPortfolioAnalysisError.set(null);
        return;
      }

      this.fetchGroupPortfolioAnalysisHistory(selectedGroupId, language);
      this.fetchGroupPortfolioAnalysis(selectedGroupId, language, null);
    });
  }

  public getGroups(): void {
    this._groupsLoading.set(true);
    this._groupError.set(null);

    this.http.get<UserManagementContext>(UserManagementService.MANAGEMENT_CONTEXT_URL)
      .pipe(
        catchError(() => {
          this._groupsLoading.set(false);
          this._groupError.set(this.i18n.t('errors.groups.load'));
          return of(null);
        })
      )
      .subscribe(context => {
        if (context == null) {
          this._groups.set([]);
          this._manageableGroups.set([]);
          this._deletableGroups.set([]);
          this._canCreateGroups.set(false);
          this._hasAdminAccess.set(false);
          this._selectedGroupId.set(null);
          this._selectedGroupUsers.set([]);
          this._selectedGroupUsersLoading.set(false);
          return;
        }

        const visibleGroups = (context.visibleGroups ?? []).filter(group => !group.isAdminGroup);
        this._groups.set(visibleGroups);
        this._manageableGroups.set(context.manageableGroups ?? []);
        this._deletableGroups.set(context.deletableGroups ?? []);
        this._canCreateGroups.set(context.canCreateGroups ?? false);
        this._hasAdminAccess.set(context.hasAdminAccess ?? false);
        this.syncSelectedGroup(visibleGroups);
        this._groupsLoading.set(false);
      });
  }

  public createGroup(groupName: string): void {
    const sanitizedGroupName = groupName.trim();
    if (sanitizedGroupName.length === 0) {
      this._groupError.set(this.i18n.t('errors.groups.required'));
      return;
    }

    const dto: CreateGroupDto = {groupName: sanitizedGroupName};
    this._createGroupLoading.set(true);
    this._groupError.set(null);

    this.http.post<ToolResponse<ManagedGroup>>(UserManagementService.GROUPS_URL, dto)
      .subscribe({
        next: value => {
          this._createGroupLoading.set(false);
          if (!value.ok) {
            this._groupError.set(value.error?.message ?? this.i18n.t('errors.groups.create'));
            return;
          }
          this.getGroups();
        },
        error: () => {
          this._createGroupLoading.set(false);
          this._groupError.set(this.i18n.t('errors.groups.create'));
        }
      });
  }

  public addUserInGroup(groupId: string, payload: UpsertGroupUserDto): void {
    this._memberMutationLoading.set(true);
    this._groupError.set(null);

    this.http.put<ToolResponse<ManagedGroup>>(
      UserManagementService.GROUP_USERS_URL.replace('{groupId}', groupId),
      payload
    ).subscribe({
      next: value => {
        this._memberMutationLoading.set(false);
        if (!value.ok) {
          this._groupError.set(value.error?.message ?? this.i18n.t('errors.groups.addUser'));
          return;
        }
        this.getGroups();
      },
      error: () => {
        this._memberMutationLoading.set(false);
        this._groupError.set(this.i18n.t('errors.groups.addUser'));
      }
    });
  }

  public deleteGroup(groupId: string): void {
    const sanitizedGroupId = groupId.trim();
    if (sanitizedGroupId.length === 0) {
      this._groupError.set(this.i18n.t('errors.groups.selectionRequired'));
      return;
    }

    this._deleteGroupLoading.set(true);
    this._groupError.set(null);

    this.http.delete<ToolResponse<void>>(`${UserManagementService.GROUPS_URL}/${sanitizedGroupId}`)
      .subscribe({
        next: value => {
          this._deleteGroupLoading.set(false);
          if (!value.ok) {
            this._groupError.set(value.error?.message ?? this.i18n.t('errors.groups.delete'));
            return;
          }
          this.getGroups();
        },
        error: () => {
          this._deleteGroupLoading.set(false);
          this._groupError.set(this.i18n.t('errors.groups.delete'));
        }
      });
  }

  public getCurrentProfile(): void {
    this._profileLoading.set(true);
    this._profileError.set(null);
    this._profileSuccess.set(null);

    this.http.get<UserProfileDetails>(UserManagementService.PROFILE_URL)
      .pipe(
        catchError(() => {
          this._profileLoading.set(false);
          this._profileError.set(this.i18n.t('errors.profile.load'));
          return of(null);
        })
      )
      .subscribe(profile => {
        this._currentProfile.set(profile);
        this.applyProfilePreferences(profile);
        this._profileLoading.set(false);
      });
  }

  public getVisibleUserProfile(userId: string): Observable<UserProfileDetails> {
    return this.http.get<UserProfileDetails>(
      UserManagementService.VISIBLE_USER_URL.replace('{userId}', userId)
    );
  }

  public updateVisibleUserProfile(userId: string, payload: UserProfileUpdateInput): Observable<UserProfileDetails> {
    return this.http.put<UserProfileDetails>(
      UserManagementService.VISIBLE_USER_URL.replace('{userId}', userId),
      payload
    );
  }

  public updateCurrentProfile(payload: UserProfileUpdateInput): void {
    this._profileSaving.set(true);
    this._profileError.set(null);
    this._profileSuccess.set(null);

    this.http.put<UserProfileDetails>(UserManagementService.PROFILE_URL, payload)
      .subscribe({
        next: profile => {
          this._currentProfile.set(profile);
          this.applyProfilePreferences(profile);
          this.refreshSelectedGroupUsers();
          this._profileSuccess.set(this.i18n.t('profile.form.saveSuccess'));
          this._profileSaving.set(false);
        },
        error: () => {
          this._profileSaving.set(false);
          this._profileError.set(this.i18n.t('errors.profile.save'));
        }
      });
  }

  public getProfileDocuments(): void {
    this._profileDocumentsLoading.set(true);
    this._profileDocumentsError.set(null);

    this.http.get<CrmDocumentSummary[]>(UserManagementService.PROFILE_DOCUMENTS_URL)
      .pipe(
        catchError(() => {
          this._profileDocuments.set([]);
          this._profileDocumentsLoading.set(false);
          this._profileDocumentsError.set(this.i18n.t('errors.profile.documentsLoad'));
          return of([]);
        })
      )
      .subscribe(documents => {
        this._profileDocuments.set(documents ?? []);
        this._profileDocumentsLoading.set(false);
      });
  }

  public downloadProfileDocument(documentId: string): Observable<Blob> {
    return this.http.get(
      UserManagementService.PROFILE_DOCUMENT_DOWNLOAD_URL.replace('{documentId}', documentId),
      {responseType: 'blob'}
    );
  }

  public deleteProfileDocument(documentId: string): Observable<void> {
    return this.http.delete<void>(
      UserManagementService.PROFILE_DOCUMENT_URL.replace('{documentId}', documentId)
    ).pipe(
      tap(() => {
        this._profileDocuments.update(documents => documents.filter(document => document.id !== documentId));
      })
    );
  }

  public getProfileConversations(): void {
    this._profileConversationsLoading.set(true);
    this._profileConversationsError.set(null);

    this.http.get<ProfileConversationSummary[]>(UserManagementService.PROFILE_CONVERSATIONS_URL)
      .pipe(
        catchError(() => {
          this._profileConversations.set([]);
          this._profileConversationsLoading.set(false);
          this._profileConversationsError.set(this.i18n.t('errors.profile.conversationsLoad'));
          return of([]);
        })
      )
      .subscribe(conversations => {
        this._profileConversations.set(conversations ?? []);
        this._profileConversationsLoading.set(false);
      });
  }

  public getProfileConversation(conversationId: string): Observable<Conversation> {
    return this.http.get<Conversation>(
      UserManagementService.PROFILE_CONVERSATION_URL.replace('{conversationId}', conversationId)
    );
  }

  public deleteProfileConversation(conversationId: string): Observable<void> {
    return this.http.delete<void>(
      UserManagementService.PROFILE_CONVERSATION_URL.replace('{conversationId}', conversationId)
    ).pipe(
      tap(() => {
        const removedDocumentIds = new Set(
          (this._profileConversations().find(conversation => conversation.id === conversationId)?.documents ?? [])
            .map(document => document.id)
        );
        this._profileConversations.update(conversations =>
          conversations.filter(conversation => conversation.id !== conversationId)
        );
        if (removedDocumentIds.size > 0) {
          this._profileDocuments.update(documents =>
            documents.filter(document => !removedDocumentIds.has(document.id))
          );
        }
      })
    );
  }

  public getAdminUserConversationDocuments(userId: string): Observable<AdminUserConversationDocumentsSummary[]> {
    return this.http.get<AdminUserConversationDocumentsSummary[]>(
      UserManagementService.ADMIN_USER_CONVERSATION_DOCUMENTS_URL.replace('{userId}', userId)
    );
  }

  public getAdminUserDeletionPreview(userId: string): Observable<AdminUserDeletionPreview> {
    return this.http.get<AdminUserDeletionPreview>(
      UserManagementService.ADMIN_USER_DELETION_PREVIEW_URL.replace('{userId}', userId)
    );
  }

  public deleteAdminUser(userId: string): Observable<void> {
    return this.http.delete<void>(
      UserManagementService.ADMIN_USER_URL.replace('{userId}', userId)
    );
  }

  public deleteAdminUserDocument(userId: string, documentId: string): Observable<void> {
    return this.http.delete<void>(
      UserManagementService.ADMIN_USER_DOCUMENT_URL
        .replace('{userId}', userId)
        .replace('{documentId}', documentId)
    );
  }

  public setLanguagePreference(language: 'en' | 'fr' | 'it'): void {
    this.i18n.setLanguage(language);
    this.persistProfilePreferences({preferredLanguage: language});
  }

  public setThemePreference(theme: 'light' | 'dark' | 'ninon'): void {
    this.themeService.setTheme(theme);
    this.persistProfilePreferences({preferredTheme: theme});
  }

  public clearProfileError(): void {
    this._profileError.set(null);
  }

  public setProfileDocumentsError(message: string | null): void {
    this._profileDocumentsError.set(message);
  }

  public setProfileConversationsError(message: string | null): void {
    this._profileConversationsError.set(message);
  }

  public clearProfileFeedback(): void {
    this._profileError.set(null);
    this._profileSuccess.set(null);
  }

  public clearGroupError(): void {
    this._groupError.set(null);
  }

  public clearGroupPortfolioAnalysisError(): void {
    this._groupPortfolioAnalysisError.set(null);
  }

  public refreshCurrentProfile(): void {
    if (!this.authService.shouldShowProtectedContent()) {
      this._currentProfile.set(null);
      this._profileLoading.set(false);
      return;
    }

    if (!this.authService.isAuthenticated()) {
      return;
    }

    this.getCurrentProfile();
  }

  public refreshSelectedGroupUsers(): void {
    if (!this.authService.shouldShowProtectedContent()) {
      this._selectedGroupUsers.set([]);
      this._selectedGroupUsersLoading.set(false);
      return;
    }

    if (!this.authService.isAuthenticated()) {
      return;
    }

    const groupId = this._selectedGroupId();
    if (!groupId) {
      this._selectedGroupUsers.set([]);
      this._selectedGroupUsersLoading.set(false);
      return;
    }

    this.getSelectedGroupUsers(groupId);
  }

  public refreshGroupPortfolioAnalysis(): void {
    const groupId = this._selectedGroupId();
    if (!groupId) {
      this._groupPortfolioAnalysis.set(null);
      this._groupPortfolioAnalysisHistory.set([]);
      this._groupPortfolioAnalysisLoading.set(false);
      this._groupPortfolioAnalysisGenerationLoading.set(false);
      return;
    }

    const language = this.i18n.language();
    this.fetchGroupPortfolioAnalysisHistory(groupId, language);
    this.fetchGroupPortfolioAnalysis(groupId, language, null);
  }

  public openGroupPortfolioAnalysis(analysisId: string | null): void {
    const groupId = this._selectedGroupId();
    if (!groupId) {
      return;
    }

    this.fetchGroupPortfolioAnalysis(groupId, this.i18n.language(), analysisId);
  }

  public generateGroupPortfolioAnalysis(): void {
    const groupId = this._selectedGroupId();
    if (!groupId) {
      this._groupPortfolioAnalysisError.set(this.i18n.t('errors.groupPortfolioAnalysis.generate'));
      return;
    }

    const language = this.i18n.language();
    this._groupPortfolioAnalysisGenerationLoading.set(true);
    this._groupPortfolioAnalysisError.set(null);

    this.consumeGroupPortfolioAnalysisGenerationStream(groupId, language)
      .then(() => {
        this._groupPortfolioAnalysisGenerationLoading.set(false);
        if (this._selectedGroupId() === groupId && this.i18n.language() === language) {
          this.refreshGroupPortfolioAnalysis();
        }
      })
      .catch(() => {
        this._groupPortfolioAnalysisGenerationLoading.set(false);
        this._groupPortfolioAnalysisError.set(this.i18n.t('errors.groupPortfolioAnalysis.generate'));
      });
  }

  public setSelectedGroup(groupId: string | null): void {
    this._selectedGroupId.set(groupId);
  }

  private applyProfilePreferences(profile: UserProfileDetails | null): void {
    const preferredLanguage = this.i18n.coerceLanguage(profile?.preferredLanguage);
    if (preferredLanguage) {
      this.i18n.setLanguage(preferredLanguage);
    }

    const preferredTheme = this.themeService.coerceTheme(profile?.preferredTheme);
    if (preferredTheme) {
      this.themeService.setTheme(preferredTheme);
    }
  }

  private persistProfilePreferences(preferences: Partial<Pick<UserProfileUpdateInput, 'preferredLanguage' | 'preferredTheme'>>): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    const profile = this._currentProfile();
    if (profile == null) {
      return;
    }

    this.updateCurrentProfile({
      firstName: profile.firstName,
      lastName: profile.lastName,
      email: profile.email,
      phone: profile.phone,
      linkedInUrl: profile.linkedInUrl,
      experienceStartDate: profile.experienceStartDate,
      jobTitle: profile.jobTitle,
      currentRoleStartDate: profile.currentRoleStartDate,
      coreSkills: profile.coreSkills,
      previousExperiences: profile.previousExperiences,
      upskillingGoals: profile.upskillingGoals,
      tags: profile.tags ?? [],
      preferredLanguage: preferences.preferredLanguage ?? profile.preferredLanguage,
      preferredTheme: preferences.preferredTheme ?? profile.preferredTheme
    });
  }

  private getSelectedGroupUsers(groupId: string): void {
    this._selectedGroupUsersLoading.set(true);
    this.http.get<ManagedGroup>(`${UserManagementService.GROUPS_URL}/${groupId}`)
      .pipe(
        catchError(() => {
          this._selectedGroupUsersLoading.set(false);
          return of(null);
        })
      )
      .subscribe(group => {
        if (group == null) {
          this._selectedGroupUsers.set([]);
          this._selectedGroupUsersLoading.set(false);
          return;
        }

        const usersById = new Map<string, ManagedUser>();
        for (const user of group.admins ?? []) {
          usersById.set(user.id, user);
        }
        for (const user of group.members ?? []) {
          if (!usersById.has(user.id)) {
            usersById.set(user.id, user);
          }
        }
        this._selectedGroupUsers.set(Array.from(usersById.values()).sort((left, right) =>
          this.userSortLabel(left).localeCompare(this.userSortLabel(right))
        ));
        this._selectedGroupUsersLoading.set(false);
      });
  }

  private fetchGroupPortfolioAnalysis(groupId: string, language: 'en' | 'fr' | 'it', analysisId: string | null): void {
    this._groupPortfolioAnalysisLoading.set(true);
    this._groupPortfolioAnalysisError.set(null);

    const params: Record<string, string> = {language};
    if (analysisId) {
      params['analysisId'] = analysisId;
    }

    this.http.get<GroupPortfolioAnalysis>(
      UserManagementService.GROUP_PORTFOLIO_ANALYSIS_URL.replace('{groupId}', groupId),
      {params}
    ).pipe(
      catchError(() => {
        this._groupPortfolioAnalysis.set(null);
        this._groupPortfolioAnalysisLoading.set(false);
        this._groupPortfolioAnalysisError.set(this.i18n.t('errors.groupPortfolioAnalysis.load'));
        return of(null);
      })
    ).subscribe(analysis => {
      if (analysis == null) {
        return;
      }

      this._groupPortfolioAnalysis.set(analysis);
      this._groupPortfolioAnalysisLoading.set(false);
    });
  }

  private async consumeGroupPortfolioAnalysisGenerationStream(groupId: string, language: 'en' | 'fr' | 'it'): Promise<void> {
    const query = new URLSearchParams({groupId, language});
    const response = await fetch(`${UserManagementService.GENERATE_GROUP_PORTFOLIO_ANALYSIS_URL}?${query.toString()}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Accept': 'text/event-stream'
      }
    });

    if (response.status === 401) {
      this.authService.handleSessionExpired();
      throw new Error('Session expired');
    }

    if (!response.ok || response.body == null) {
      throw new Error(`Unexpected generate response status: ${response.status}`);
    }

    const reader = response.body.getReader();

    try {
      while (true) {
        const {done} = await reader.read();
        if (done) {
          return;
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  private fetchGroupPortfolioAnalysisHistory(groupId: string, language: 'en' | 'fr' | 'it'): void {
    this.http.get<GroupPortfolioAnalysisSummary[]>(
      UserManagementService.GROUP_PORTFOLIO_ANALYSIS_HISTORY_URL.replace('{groupId}', groupId),
      {params: {language}}
    ).pipe(
      catchError(() => {
        this._groupPortfolioAnalysisHistory.set([]);
        return of([]);
      })
    ).subscribe(history => {
      this._groupPortfolioAnalysisHistory.set(history ?? []);
    });
  }

  private userSortLabel(user: ManagedUser): string {
    const fullName = [user.firstName, user.lastName]
      .filter((value): value is string => !!value && value.trim().length > 0)
      .join(' ')
      .trim();
    return `${fullName || user.email || user.id}|${user.id}`.toLowerCase();
  }

  private syncSelectedGroup(groups: Group[]): void {
    if (groups.length === 0) {
      this._selectedGroupId.set(null);
      return;
    }

    const selectedGroupId = this._selectedGroupId();
    const preferredGroupId = groups.find(group => !group.isAdminGroup)?.id ?? groups[0].id;
    if (selectedGroupId == null || !groups.some(group => group.id === selectedGroupId)) {
      this._selectedGroupId.set(preferredGroupId);
      return;
    }

    const selectedGroup = groups.find(group => group.id === selectedGroupId);
    if (selectedGroup?.isAdminGroup && preferredGroupId !== selectedGroupId) {
      this._selectedGroupId.set(preferredGroupId);
    }
  }
}
